import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getUser } from '../../services/userService';
import Loader from '../common/Loader';
import ProfileAvatar from '../common/ProfileAvatar';
import { formatMMDDYYYY } from '../../utils/dateUtils';

const ROLE_MAP = { 1: 'SPED Director', 2: 'SPED Teacher' };

const statusColors = {
  active: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800',
  inactive: 'bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800',
  suspended: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
};

export default function ViewUser() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await getUser(id);
        setUser(data.data);
      } catch {
        navigate('/users');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id, navigate]);

  if (loading) return <Loader text="Loading user..." />;
  if (!user) return null;

  return (
    <div className="mx-auto w-full min-w-0 max-w-[min(100%,56rem)] space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/users" className="p-2 rounded-xl text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors flex-shrink-0">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">User Details</h1>
        </div>
        <Link
          to={`/users/${id}/edit`}
          className="flex items-center gap-2 px-4 py-2 rounded-xl border border-indigo-200 dark:border-indigo-500/40 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-600/10 text-sm font-medium transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
          Edit
        </Link>
      </div>

      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-2xl overflow-hidden">
        {/* Avatar header */}
        <div className="bg-gradient-to-br from-indigo-50 dark:from-indigo-600/20 to-white dark:to-slate-800 px-4 sm:px-6 py-6 sm:py-8 flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-5 text-center sm:text-left">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden flex-shrink-0 shadow-lg shadow-indigo-500/20">
            <ProfileAvatar
              type="user"
              id={user.id}
              variant="solid"
              hasProfileImage={user.has_profile_image}
            />
          </div>
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white break-words">
              {user.first_name} {user.middle_name ? user.middle_name + ' ' : ''}{user.last_name}
            </h2>
            <p className="text-gray-500 dark:text-slate-400 text-sm mt-0.5 break-all">{user.email}</p>
            <div className="flex items-center gap-2 mt-2 justify-center sm:justify-start flex-wrap">
              <span className="px-2.5 py-1 rounded-full bg-indigo-100 dark:bg-indigo-600/20 text-indigo-600 dark:text-indigo-300 text-xs font-medium border border-indigo-200 dark:border-indigo-500/30">
                {ROLE_MAP[user.role] || 'No role'}
              </span>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium border capitalize ${statusColors[user.is_active] || ''}`}>
                {user.is_active}
              </span>
            </div>
          </div>
        </div>

        {/* Details grid */}
        <div className="px-4 sm:px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
          <InfoField label="Mobile" value={user.mobile || '—'} />
          {user.school_name && <InfoField label="School" value={user.school_name} />}
          <InfoField label="Hire Date" value={formatMMDDYYYY(user.hire_date) || '—'} />
          <InfoField label="Termination Date" value={formatMMDDYYYY(user.termination_date) || '—'} />
          <InfoField label="Member Since" value={formatMMDDYYYY(user.created_at) || '—'} />
          <InfoField label="Address" value={
            [user.address_line1, user.address_line2].filter(Boolean).join(', ') || '—'
          } />
          <InfoField label="City / State" value={
            [user.city, user.state, user.zipcode].filter(Boolean).join(', ') || '—'
          } />
        </div>

        {user.user_notes && user.user_notes.length > 0 && (
          <div className="px-4 sm:px-6 pb-5 border-t border-gray-200 dark:border-slate-700 pt-4">
            <p className="text-gray-400 dark:text-slate-500 text-xs font-semibold uppercase tracking-wider mb-3">Notes</p>
            <div className="space-y-2">
              {user.user_notes.map((n, i) => (
                <div key={i} className="px-3 py-2 bg-gray-50 dark:bg-slate-700/40 rounded-lg text-gray-700 dark:text-slate-300 text-sm">
                  {n.note || JSON.stringify(n)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-400 dark:text-slate-500 font-medium uppercase tracking-wider mb-1">{label}</p>
      <p className="text-gray-700 dark:text-slate-200 text-sm break-words">{value}</p>
    </div>
  );
}
